import type { Product } from '~/stores/product/types';

export enum ButtonType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export enum CmsBackgroundColor {
  GRAY = 'gray',
  TRANSPARENT = 'transparent',
  WHITE = 'white',
}

export enum CmsImagePosition {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum CmsImageSize {
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
}

export enum CmsMaxWidth {
  FULL = 'full',
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
}

export enum CmsPadding {
  LARGE = 'large',
  MEDIUM = 'medium',
  NONE = 'none',
  SMALL = 'small',
}

export enum CmsTextAlignment {
  CENTER = 'center',
  JUSTIFY = 'justify',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum NodeType {
  CODE = 'code',
  HEADING = 'heading',
  IMAGE = 'image',
  LINK = 'link',
  LIST = 'list',
  LIST_ITEM = 'list-item',
  PARAGRAPH = 'paragraph',
  QUOTE = 'quote',
  TEXT = 'text',
}

export enum PageSectionType {
  POPULAR_CATEGORIES = 'popular-categories',
  PRODUCT_SELECTION = 'product-selection',
  PROMO_BANNER = 'promo-banner',
  RICH_TEXT = 'rich-text',
  TESTIMONIALS = 'testimonials',
  TEXT_WITH_IMAGE = 'text-with-image',
}

export interface BaseNode {
  type: NodeType;
}

export interface CmsButton {
  title: string;
  type: ButtonType;
  url: string;
}

export interface CmsContact {
  description?: string;
  icon: CmsImage | null;
  id: number;
  title: string;
}

export interface CmsCopyright {
  copyright: string;
  links: Link[];
  socials: CmsSocial[];
}

export interface CmsImage {
  alt_text: string | null;
  caption: string;
  id: number | string;
  name: string;
  url: string;
}

export interface CmsPage {
  hero: PageHero;
  meta_description?: string;
  meta_title?: string;
  sections: PageSection[];
  slug: string;
  title: string;
}

export interface CmsSocial {
  id: number;
  link: string;
  logo: CmsImage;
  name: string;
}

export interface CodeBlockNode extends BaseNode {
  children: DefaultInlineNode[];
  type: NodeType.CODE;
}

export type DefaultInlineNode = LinkInlineNode | TextInlineNode;

export interface Footer {
  contacts: CmsContact[];
  copyright: CmsCopyright;
  sections: FooterSection[];
}

export interface FooterSection {
  heading: string;
  id: number;
  links: Link[];
}

export interface HeadingBlockNode extends BaseNode {
  children: DefaultInlineNode[];
  level: 1 | 2 | 3 | 4 | 5 | 6;
  type: NodeType.HEADING;
}

export interface ImageBlockNode extends BaseNode {
  children: [TextInlineNode];
  image: CmsImage;
  type: NodeType.IMAGE;
}

export interface Link {
  id: number;
  name: string;
  url: string;
}

export interface LinkInlineNode extends BaseNode {
  children: TextInlineNode[];
  type: NodeType.LINK;
  url: string;
}

export interface ListBlockNode extends BaseNode {
  children: (ListBlockNode | ListItemInlineNode)[];
  format: 'ordered' | 'unordered';
  type: NodeType.LIST;
}

export interface ListItemInlineNode extends BaseNode {
  children: DefaultInlineNode[];
  type: NodeType.LIST_ITEM;
}

export interface Menu {
  menu_sections: MenuSection[];
  title: string;
}

export interface MenuSection {
  id: number;
  links: Link[];
  title: string;
  url?: string;
}

export type NonTextInlineNode = Exclude<DefaultInlineNode, TextInlineNode> | ListItemInlineNode;

export interface PageHero {
  cover: CmsImage;
  cover_mobile?: CmsImage;
  cta?: CmsButton;
  main_picture: CmsImage;
  subtitle?: string;
  title: string;
}

export interface PagePopularCategories {
  categories: PagePopularCategory[];
  id: string;
  subtitle?: string;
  title: string;
  type: PageSectionType.POPULAR_CATEGORIES;
}

export interface PagePopularCategory {
  id: string;
  image: CmsImage;
  item_count: number;
  slug: string;
  title: string;
}

export interface PageProductSelection {
  id: string;
  products: Product[];
  subtitle?: string;
  title: string;
  type: PageSectionType.PRODUCT_SELECTION;
}

export interface PagePromoBanner {
  cover?: CmsImage;
  cover_mobile?: CmsImage;
  cta?: CmsButton;
  expired_at?: string;
  id: string;
  subtitle?: string;
  title: string;
  type: PageSectionType.PROMO_BANNER;
}

export interface PageRichText {
  background_color: CmsBackgroundColor;
  content: RichTextContent;
  id: string;
  max_width: CmsMaxWidth;
  padding: CmsPadding;
  text_alignment: CmsTextAlignment;
  type: PageSectionType.RICH_TEXT;
}

export type PageSection =
  | PagePopularCategories
  | PageProductSelection
  | PagePromoBanner
  | PageRichText
  | PageTestimonials
  | PageTextWithImage;

export interface PageTestimonial {
  avatar?: CmsImage;
  content: string;
  full_name: string;
  id: string;
  job_title: string;
  note: number;
}

export interface PageTestimonials {
  id: string;
  subtitle?: string;
  testimonials: PageTestimonial[];
  title: string;
  type: PageSectionType.TESTIMONIALS;
}

export interface PageTextWithImage {
  background_color: CmsBackgroundColor;
  content: RichTextContent;
  cta?: CmsButton;
  id: string;
  image: CmsImage;
  image_position: CmsImagePosition;
  image_size: CmsImageSize;
  padding: CmsPadding;
  title?: string;
  type: PageSectionType.TEXT_WITH_IMAGE;
}

export interface ParagraphBlockNode extends BaseNode {
  children: DefaultInlineNode[];
  type: NodeType.PARAGRAPH;
}

export interface QuoteBlockNode extends BaseNode {
  children: DefaultInlineNode[];
  type: NodeType.QUOTE;
}

export interface Reassurance {
  reassurance_items: ReassuranceItem[];
}

export interface ReassuranceItem {
  icon: CmsImage;
  id: number;
  title: string;
}

export type RichTextContent = (NonTextInlineNode | RootNode)[];

export type RootNode =
  | CodeBlockNode
  | HeadingBlockNode
  | ImageBlockNode
  | ListBlockNode
  | ParagraphBlockNode
  | QuoteBlockNode;

export interface TextInlineNode extends BaseNode {
  bold?: boolean;
  code?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  text: string;
  type: NodeType.TEXT;
  underline?: boolean;
}
